import { inject } from "mobx-react";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { isPWSSessionBackend, parsePWSError } from "@inferno/renderer-shared-core";
import { ILog } from "@ihr-radioedit/inferno-core";
import { Button, ButtonKind, FauxButton } from "../../../../../core/ui";
import { FORM_ERRORS, SimpleInput } from "../../forms";
import { RecaptchaForm, RecaptchaFromOnSubmit } from "../../forms/RecaptchaForm.component";
import { useCoastAlerts } from "../common/coast-alert/CoastAlerts.hook";
import { CoastAlertsDisplay } from "../common/coast-alert/CoastAlertsDisplay.components";
import { UsersButton } from "../common/UsersButton.components";
import type { Store } from "@inferno/renderer-shared-core";

const log = ILog.logger("Coast Forgot Password Component");

interface ForgotPasswordInputs {
  email: string;
}

interface CoastForgotPasswordProps {
  goBack: () => void;
  store?: Store;
  email: string;
}

export const CoastForgotPassword = inject("store")(({ goBack, email, store }: CoastForgotPasswordProps) => {
  const passwordFormMethods = useForm<ForgotPasswordInputs>({ defaultValues: { email } });
  const { register, errors } = passwordFormMethods;

  const { alerts, setErrorAlert, clearAllAlerts, setSuccessAlert } = useCoastAlerts();
  const [isProcessing, setIsProcessing] = useState(false);

  const session = store?.session?.currentSession;

  if (!store || !isPWSSessionBackend(session)) {
    return null;
  }

  const preSubmit = useCallback(() => {
    clearAllAlerts();
    setIsProcessing(true);
  }, [clearAllAlerts]);

  const onSubmit: RecaptchaFromOnSubmit<ForgotPasswordInputs> = useCallback(
    async ({ email: inputEmail }, token) => {
      try {
        await session.memberService.forgotPassword(inputEmail, token);
        setSuccessAlert("We have emailed you your password reset link");
      } catch (e) {
        log.error(e);
        setErrorAlert(parsePWSError(e));
      } finally {
        setIsProcessing(false);
      }
    },
    [session.memberService, setErrorAlert, setSuccessAlert],
  );

  return (
    <section className="component-forgot-password">
      <CoastAlertsDisplay alerts={alerts} />

      <FormProvider {...passwordFormMethods}>
        <RecaptchaForm
          siteKey={store.env.COAST_RECAPTCHA_V3_PUBLIC_KEY}
          setIsProcessing={setIsProcessing}
          action="forgot_password"
          setError={setErrorAlert}
          preSubmit={preSubmit}
          onSubmit={onSubmit}
        >
          <SimpleInput
            placeholder="Enter your email"
            rules={{ required: FORM_ERRORS.required }}
            error={errors.email}
            register={register}
            label="Email"
            name="email"
            type="email"
          />

          <UsersButton
            disabled={!!errors.email || isProcessing}
            isProcessing={isProcessing}
            type="submit"
            label="Send Reset Password email"
          >
            Reset Password
          </UsersButton>
        </RecaptchaForm>
      </FormProvider>

      <Button click={goBack} className="coast-button-text back-to-login" kind={ButtonKind.CUSTOMIZED}>
        Back To Login
      </Button>

      <p>
        Need Help? &nbsp;
        <FauxButton to="https://help.coasttocoastam.com/" className="coast-button-text" kind={ButtonKind.CUSTOMIZED}>
          Contact Customer Service
        </FauxButton>
      </p>
    </section>
  );
});
